import { onlineManager, QueryClient, QueryKey } from "@tanstack/react-query";
import type { InvalidateQueryFilters } from "@tanstack/react-query";
import {
  logGrazingOfflineReplay,
  replayErrorPayload,
} from "common/utils/grazingOfflineReplayLog";

type OfflineReplayStatus = "idle" | "replaying" | "paused";

let status: OfflineReplayStatus = "idle";
const deferredInvalidations = new Map<string, InvalidateQueryFilters>();

function keyFor(filters: InvalidateQueryFilters): string {
  return JSON.stringify(filters.queryKey ?? []);
}

function summarizeMutationVariables(
  variables: unknown
): Record<string, unknown> {
  if (!variables || typeof variables !== "object") return {};

  if (
    "manualDryMatterInput" in variables &&
    typeof variables.manualDryMatterInput === "object" &&
    variables.manualDryMatterInput
  ) {
    const reading = variables as {
      guid?: string;
      manualDryMatterInput: {
        field?: string;
        event?: {
          eventType?: string;
          dateTimeIn?: string;
          dateTimeOut?: string;
          animalGroupGuid?: string;
        };
      };
    };
    return {
      guid: reading.guid,
      field: reading.manualDryMatterInput.field,
      eventType: reading.manualDryMatterInput.event?.eventType,
      dateTimeIn: reading.manualDryMatterInput.event?.dateTimeIn,
      dateTimeOut: reading.manualDryMatterInput.event?.dateTimeOut,
      animalGroupGuid: reading.manualDryMatterInput.event?.animalGroupGuid,
    };
  }

  if ("readingId" in variables) {
    const deleteVariables = variables as {
      readingId?: string;
      fieldId?: string;
      isGrazing?: boolean;
      animalGroupGuid?: string;
    };
    return {
      readingId: deleteVariables.readingId,
      fieldId: deleteVariables.fieldId,
      isGrazing: deleteVariables.isGrazing,
      animalGroupGuid: deleteVariables.animalGroupGuid,
    };
  }

  if ("openEventGuid" in variables || "closeEventGuid" in variables) {
    const moveVariables = variables as {
      openEventGuid?: string;
      closeEventGuid?: string;
      sourceFieldGuid?: string;
      destFieldGuid?: string;
      movedToShed?: boolean;
      animalGroupGuid?: string;
      moveDate?: Date | string;
    };
    return {
      openEventGuid: moveVariables.openEventGuid,
      closeEventGuid: moveVariables.closeEventGuid,
      sourceFieldGuid: moveVariables.sourceFieldGuid,
      destFieldGuid: moveVariables.destFieldGuid,
      movedToShed: moveVariables.movedToShed,
      animalGroupGuid: moveVariables.animalGroupGuid,
      moveDate:
        moveVariables.moveDate instanceof Date
          ? moveVariables.moveDate.toISOString()
          : moveVariables.moveDate,
    };
  }

  return {};
}

function logMutationQueueSnapshot(
  queryClient: QueryClient,
  phase: string
): void {
  const mutations = queryClient.getMutationCache().getAll();
  logGrazingOfflineReplay(phase, {
    mutationCount: mutations.length,
    mutations: mutations.map((mutation, index) => ({
      index,
      mutationKey: mutation.options.mutationKey,
      scope: mutation.options.scope,
      status: mutation.state.status,
      isPaused: mutation.state.isPaused,
      failureCount: mutation.state.failureCount,
      submittedAt: mutation.state.submittedAt,
      ...summarizeMutationVariables(mutation.state.variables),
    })),
  });
}

export function getOfflineReplayStatus(): OfflineReplayStatus {
  return status;
}

export function isOfflineReplayActive(): boolean {
  return status === "replaying" || status === "paused";
}

export function deferQueryInvalidation(filters: InvalidateQueryFilters): void {
  deferredInvalidations.set(keyFor(filters), filters);
  logGrazingOfflineReplay("replay.invalidation.deferred", {
    queryKey: filters.queryKey,
    deferredInvalidationCount: deferredInvalidations.size,
  });
}

export function invalidateUnlessReplaying(
  queryClient: QueryClient,
  filters: InvalidateQueryFilters
): Promise<void> {
  if (isOfflineReplayActive()) {
    deferQueryInvalidation(filters);
    return Promise.resolve();
  }
  return queryClient.invalidateQueries(filters);
}

async function flushDeferredInvalidations(
  queryClient: QueryClient
): Promise<void> {
  const invalidations = Array.from(deferredInvalidations.values());
  deferredInvalidations.clear();
  logGrazingOfflineReplay("replay.invalidation.flush", {
    count: invalidations.length,
    queryKeys: invalidations.map((filters) => filters.queryKey),
  });
  await Promise.all(
    invalidations.map((filters) => queryClient.invalidateQueries(filters))
  );
}

function normalizeReplayError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export async function resumePausedMutationsWithoutPartialRefetch(
  queryClient: QueryClient,
  queryKeysToRefreshAfterReplay: QueryKey[] = []
): Promise<void> {
  status = "replaying";
  logMutationQueueSnapshot(queryClient, "replay.queue.before-resume");
  logGrazingOfflineReplay("replay.resume.start", {
    queryKeysToRefreshAfterReplay,
  });
  let replayError: Error | undefined;
  try {
    await queryClient.resumePausedMutations();
  } catch (error) {
    replayError = normalizeReplayError(error);
    logGrazingOfflineReplay("replay.resume.error", {
      ...replayErrorPayload(error),
    });
  }
  if (!onlineManager.isOnline()) {
    status = "paused";
    logGrazingOfflineReplay("replay.resume.paused", {
      replayError: replayError ? replayErrorPayload(replayError) : undefined,
    });
    logMutationQueueSnapshot(queryClient, "replay.queue.after-paused");
    if (replayError) throw replayError;
    return;
  }
  logMutationQueueSnapshot(queryClient, "replay.queue.after-resume");
  for (const queryKey of queryKeysToRefreshAfterReplay) {
    deferQueryInvalidation({ queryKey });
  }
  status = "idle";
  logGrazingOfflineReplay("replay.resume.flush-start", {
    deferredInvalidationCount: deferredInvalidations.size,
  });
  await flushDeferredInvalidations(queryClient);
  logGrazingOfflineReplay("replay.resume.done");
  if (replayError) throw replayError;
}

export function resetOfflineReplayCoordinatorForTests(): void {
  status = "idle";
  deferredInvalidations.clear();
}
